import isEqual from "lodash/isEqual";

const fields = ["first_name", "last_name", "full_name", "email", "phone_number", "address", "profession", "country", "state", "city", "religion", "caste", "gotra", "gender", "birth_date", "birth_place", "is_alive", "date_of_passing", "father_id", "mother_id", "spouse_id", "spouse_ids", "children_ids", "social_links", "notes"];
export const draftDetails = (person) => Object.fromEntries(fields.filter((field) => person[field] !== undefined).map((field) => [field, person[field]]));
export const draftMemberId = (member) => member.member_id || `draft:${member.node_id}`;
export function communityProposals(value, castes, gotras) {
  const same = (a, b) => (a || "").trim().toLocaleLowerCase() === (b || "").trim().toLocaleLowerCase();
  const religion = value.religion, caste = (value.caste || "").trim(), gotra = (value.gotra || "").trim(), proposals = [];
  if (caste && !castes.some(item => item.religion === religion && same(item.name, caste))) proposals.push({ kind: "caste", religion, name: caste });
  if (gotra && !gotras.some(item => item.religion === religion && same(item.caste, caste) && same(item.name, gotra))) proposals.push({ kind: "gotra", religion, caste, name: gotra });
  return proposals;
}
export function comparableMember(person) {
  const result = draftDetails(person);
  result.full_name = [person.first_name, person.last_name].filter(Boolean).join(" ").trim() || person.full_name || "";
  result.children_ids = [...new Set(person.children_ids || [])].sort();
  result.spouse_ids = [...new Set(person.spouse_ids || (person.spouse_id ? [person.spouse_id] : []))].sort();
  result.spouse_id = result.spouse_ids[0] || null;
  return result;
}
export function draftCounts(record) {
  const added = record.members.filter((member) => !member.member_id).length;
  const edited = record.members.filter((member) => member.member_id && !isEqual(comparableMember(member.proposal), comparableMember(record.member_baselines?.[member.member_id] || {}))).length;
  return { added, edited, total: added + edited };
}
export function previewMembers(record, persons = []) {
  const graph = Object.fromEntries(persons.map((person) => [person.id, { ...person, children_ids: [...(person.children_ids || [])], spouse_ids: [...(person.spouse_ids || [])] }]));
  for (const member of record.members) {
    const id = draftMemberId(member);
    graph[id] = { ...graph[id], ...member.proposal, id, children_ids: [...(member.proposal.children_ids || [])], spouse_ids: [...(member.proposal.spouse_ids || [])] };
  }
  // Detach removed inverse links before drawing the proposed relationships.
  for (const member of record.members) {
    const old = persons.find((person) => person.id === member.member_id);
    if (!old) continue;
    const id = draftMemberId(member), proposed = graph[id];
    for (const slot of ["father_id", "mother_id"]) {
      const previous = old[slot];
      if (previous && previous !== proposed[slot] && graph[previous]) graph[previous].children_ids = graph[previous].children_ids.filter((child) => child !== id);
    }
    const slot = old.gender === "Female" ? "mother_id" : "father_id";
    for (const child of old.children_ids || []) if (!proposed.children_ids.includes(child) && graph[child]?.[slot] === id) graph[child][slot] = null;
    for (const spouse of old.spouse_ids || (old.spouse_id ? [old.spouse_id] : [])) if (!proposed.spouse_ids.includes(spouse) && graph[spouse]) graph[spouse].spouse_ids = graph[spouse].spouse_ids.filter((relative) => relative !== id);
  }
  for (const person of Object.values(graph)) {
    for (const parent of [person.father_id, person.mother_id]) if (graph[parent] && !graph[parent].children_ids.includes(person.id)) graph[parent].children_ids.push(person.id);
    for (const spouse of person.spouse_ids) if (graph[spouse] && !graph[spouse].spouse_ids.includes(person.id)) graph[spouse].spouse_ids.push(person.id);
    const slot = person.gender === "Female" ? "mother_id" : "father_id";
    for (const child of person.children_ids) if (graph[child] && !graph[child][slot]) graph[child][slot] = person.id;
  }
  for (const parent of Object.values(graph)) {
    const old = persons.find((person) => person.id === parent.id);
    if (old?.spouse_ids?.length || old?.spouse_id) continue;
    const ownSlot = parent.gender === "Female" ? "mother_id" : "father_id";
    for (const spouseId of parent.spouse_ids) {
      const spouse = graph[spouseId]; if (!spouse) continue;
      const slot = spouse.gender === "Female" ? "mother_id" : "father_id";
      for (const child of Object.values(graph)) {
        const edit = record.members.find((member) => member.member_id === child.id);
        const base = persons.find((person) => person.id === child.id);
        const explicitlyChanged = edit && edit.proposal[slot] !== base?.[slot];
        if (child[ownSlot] === parent.id && !child[slot] && !explicitlyChanged) child[slot] = spouseId;
      }
    }
  }
  for (const person of Object.values(graph)) person.children_ids = [];
  for (const child of Object.values(graph)) for (const parent of [child.father_id, child.mother_id]) if (graph[parent]) graph[parent].children_ids.push(child.id);
  const included = new Set(), stack = record.members.map(draftMemberId);
  while (stack.length) {
    const id = stack.pop(); if (included.has(id) || !graph[id]) continue;
    included.add(id); const member = graph[id];
    stack.push(...[member.father_id, member.mother_id, ...member.children_ids, ...member.spouse_ids].filter(Boolean));
  }
  return [...included].map((id) => graph[id]);
}
